import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { getCheckoutStatus } from "../services/api";
import Seo from "../components/Seo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const { t, clearCart } = useApp();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("failed"); return; }
      attempts.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setOrderId(data.order_id);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { cleared.current = true; clearCart(); }
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-20 min-h-screen bg-cream flex items-center justify-center">
      <Seo title={t("checkout.title")} description={t("checkout.sub")} path="/checkout/success" noindex />
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="checkout-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-wine animate-spin mb-6" />
            <h1 className="font-serif text-3xl text-ink">{t("checkout.checking")}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-6" data-testid="payment-success-icon" />
            <h1 className="font-serif text-3xl text-ink" data-testid="payment-success-title">{t("checkout.successTitle")}</h1>
            <p className="mt-3 text-ink/60">{t("checkout.successSub")}</p>
            {orderId && <p className="mt-4 text-sm text-ink/50">{t("checkout.orderNr")}: <span className="font-mono text-wine" data-testid="order-number">{orderId.slice(0, 8)}</span></p>}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              {user && <Link to="/account"><Button variant="outline" className="border-wine/30 text-wine">{t("auth.myOrders")}</Button></Link>}
              <Link to="/store"><Button className="bg-wine hover:bg-wine-dark text-white">{t("common.continueShopping")}</Button></Link>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-destructive mb-6" />
            <h1 className="font-serif text-3xl text-ink" data-testid="payment-failed-title">{t("checkout.failTitle")}</h1>
            <p className="mt-3 text-ink/60">{t("checkout.failSub")}</p>
            <Link to="/checkout"><Button className="mt-8 bg-wine hover:bg-wine-dark text-white">{t("checkout.backToCheckout")}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
